import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseUnits, maxUint256 } from 'viem';
import { useAccount, useSwitchChain } from 'wagmi';
import { useQueryClient } from '@tanstack/react-query';
import {
  DEPOSIT_CHAIN_BY_ID,
  INK_DEPOSIT_CHAIN,
  getChainToken,
  DepositTokenSymbol,
} from '../config/chains';
import { GAS_AIRDROP_CONFIG } from '../config/oft';
import {
  BridgeMode,
  NadoBridgeProgressData,
  NadoDepositButtonState,
  NadoDepositFormActions,
  NadoDepositFormData,
} from '../types';
import { buildExtraOptions } from '../utils/buildExtraOptions';
import { useExecuteApproveAllowanceForProduct } from 'client/hooks/execute/useExecuteApproveAllowanceForProduct';
import { useExecuteDepositCollateral } from 'client/hooks/execute/useExecuteDepositCollateral';
import { useTokenAllowanceForProduct } from 'client/hooks/query/useTokenAllowanceForProduct';
import { tokenAllowanceQueryKey } from 'client/hooks/query/useTokenAllowance';
import { useOnChainTransactionState } from 'client/hooks/query/useOnChainTransactionState';
import { useInkGasBalance } from './useInkGasBalance';
import { useNeedsGasAirdrop } from './useNeedsGasAirdrop';
import { useCctpAllowance } from './useCctpAllowance';
import { useCctpApprove } from './useCctpApprove';
import { useCctpAttestation } from './useCctpAttestation';
import { useCctpBurn } from './useCctpBurn';
import { useCctpReceive } from './useCctpReceive';
import { useOftAllowance } from './useOftAllowance';
import { useOftApprove } from './useOftApprove';
import { useOftBridgeStatus } from './useOftBridgeStatus';
import { useOftQuoteOFT } from './useOftQuoteOFT';
import { useOftQuoteSend } from './useOftQuoteSend';
import { useOftSend } from './useOftSend';
import { usePendingBridges } from './usePendingBridges';
import { useSourceTokenBalance } from './useSourceTokenBalance';
import { useStargateAllowance } from './useStargateAllowance';
import { useStargateApprove } from './useStargateApprove';
import { useStargateQuoteSend } from './useStargateQuoteSend';
import { useStargateSend } from './useStargateSend';
import { useAcrossQuote, useAcrossExecute } from './useAcrossBridge';
import { useSponsoredNadoTransactions } from './useSponsoredNadoTransactions';
import { selectAcrossAvailableBalance } from '../utils/acrossBalanceDisplay';
import { assertCanSubmitNadoTransaction } from '../utils/nadoExecutionPath';
import {
  calculateAcrossAutoDepositAmount,
  calculateAcrossGrossInputAmount,
  deriveAcrossArrivalState,
  isFundingTokenSymbol,
  resolveFundingRoute,
  toNadoProductAmount,
} from '../config/fundingRoutes';
import { getNadoDepositErrorMessage } from '../utils/getNadoDepositErrorMessage';
import { clientEnv } from 'common/environment/clientEnv';
import { useIsPrivyEmbeddedWallet } from 'client/hooks/util/useIsPrivyEmbeddedWallet';
import {
  canStartNadoFundingAutomationStep,
  getNadoFundingAutomationStatus,
  shouldSubmitNadoFundingAutomationStep,
  shouldStopNadoFundingAutomation,
} from 'client/modules/collateral/nadoFundingAutomation/nadoFundingAutomation';

interface UseNadoDepositFormParams {
  selectedChainId: number;
  selectedTokenSymbol: string;
  sourceBalanceAddress?: `0x${string}`;
}

interface UseNadoDepositFormReturn {
  formData: NadoDepositFormData;
  formActions: NadoDepositFormActions;
  bridgeProgress: NadoBridgeProgressData | undefined;
}

/**
 * Master orchestration hook for the Nado deposit flow.
 *
 * Approve and deposit are SEPARATE button states. After the user clicks
 * "Approve", the allowance query (polling every 3s) detects the new
 * allowance and the state machine transitions to "Deposit" automatically.
 * No need to chain txs or wait for receipts.
 */
export function useNadoDepositForm({
  selectedChainId,
  selectedTokenSymbol,
  sourceBalanceAddress,
}: UseNadoDepositFormParams): UseNadoDepositFormReturn {
  const queryClient = useQueryClient();
  const { address, chainId: connectedChainId } = useAccount();
  const { switchChainAsync, isPending: isSwitchingChain } = useSwitchChain();
  const isPrivyEmbeddedWallet = useIsPrivyEmbeddedWallet();
  const automationSubmitInFlightRef = useRef(false);
  const lastAutomationSubmittedStateRef = useRef<NadoDepositButtonState | undefined>();
  const formSessionVersionRef = useRef(0);

  const [amount, setAmount] = useState('');
  const [bridgeTxHash, setBridgeTxHash] = useState<string | undefined>();
  const [acrossFillTxHash, setAcrossFillTxHash] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [errorDetails, setErrorDetails] = useState<string | undefined>();
  const [cctpReceiveComplete, setCctpReceiveComplete] = useState(false);
  const [acrossFillConfirmed, setAcrossFillConfirmed] = useState(false);
  const [expectedOutputAmount, setExpectedOutputAmount] = useState<bigint | undefined>();
  const [preBridgeInkBalance, setPreBridgeInkBalance] = useState<bigint | undefined>();
  const [bridgeRecipient, setBridgeRecipient] = useState<`0x${string}` | undefined>();
  const [bridgeSubmittedAtMs, setBridgeSubmittedAtMs] = useState<number | undefined>();
  const [arrivalNowMs, setArrivalNowMs] = useState(() => Date.now());
  const [allowPartialDeposit, setAllowPartialDeposit] = useState(false);
  const [isFundingAutomationActive, setIsFundingAutomationActive] = useState(false);
  // User-selectable bridge mode for USDC (fast = Across, free = CCTP)
  const [bridgeMode, setBridgeMode] = useState<BridgeMode>('fast');

  const chainConfig = DEPOSIT_CHAIN_BY_ID[selectedChainId];
  const isSameChain = selectedChainId === INK_DEPOSIT_CHAIN.chainId;
  const isCorrectChain = connectedChainId === selectedChainId;
  const isOnInk = connectedChainId === INK_DEPOSIT_CHAIN.chainId;
  const balanceAddress = sourceBalanceAddress ?? address;
  const fundingRoute = isFundingTokenSymbol(selectedTokenSymbol)
    ? resolveFundingRoute(selectedChainId, selectedTokenSymbol)
    : undefined;
  const isAcrossFunding = !!fundingRoute && !isSameChain;

  // --- Derive productId and token address from selected token symbol ---
  const selectedToken = fundingRoute
    ? getChainToken(INK_DEPOSIT_CHAIN.chainId, fundingRoute.inkTokenSymbol)
    : getChainToken(selectedChainId, selectedTokenSymbol as DepositTokenSymbol);
  const productId = fundingRoute?.productId ?? selectedToken?.productId ?? 0;
  // Use token-specific address for balance queries (e.g. USDC has different address than USDT0)
  const selectedTokenAddress = fundingRoute?.sourceTokenAddress ?? selectedToken?.tokenAddress;

  // --- Bridge type: 'oft' | 'cctp' | 'across' | 'stargate' | 'none' ---
  // USDC cross-chain has a user-selectable mode (fast=Across, free=CCTP)
  const configBridgeType = selectedToken?.bridgeType ?? 'none';
  const isUsdcCrossChain = !isAcrossFunding && configBridgeType === 'across' && !isSameChain;
  const isCctp = !isAcrossFunding && (isUsdcCrossChain ? bridgeMode === 'free' : configBridgeType === 'cctp');
  const isOft = !isAcrossFunding && configBridgeType === 'oft';
  const isStargate = !isAcrossFunding && configBridgeType === 'stargate';
  const isAcross = isAcrossFunding || (isUsdcCrossChain ? bridgeMode === 'fast' : false);

  // --- Balance queries ---
  const { data: balanceData } = useSourceTokenBalance(selectedChainId, balanceAddress, selectedTokenAddress);
  // Ink balance for the deposit token — used after bridge to cap deposit to actual received amount
  const inkToken = getChainToken(
    INK_DEPOSIT_CHAIN.chainId,
    fundingRoute?.inkTokenSymbol ?? (selectedTokenSymbol as DepositTokenSymbol),
  );
  const { data: inkBalanceData } = useSourceTokenBalance(
    !isSameChain ? INK_DEPOSIT_CHAIN.chainId : undefined,
    bridgeRecipient ?? address,
    inkToken?.tokenAddress,
  );
  const { data: inkGasBalanceData } = useInkGasBalance(address);
  const { needsAirdrop: needsGasAirdrop } = useNeedsGasAirdrop(address);

  // --- Parse amount ---
  const parsedAmount = useMemo(() => {
    if (!amount || !chainConfig) return undefined;
    try {
      return parseUnits(amount, chainConfig.tokenDecimals);
    } catch {
      return undefined;
    }
  }, [amount, chainConfig]);

  // --- OFT quotes (cross-chain OFT only) ---
  const includeGasAirdrop = needsGasAirdrop && !isSameChain;

  const gasAirdrop = useMemo(
    () =>
      includeGasAirdrop && address && isOft
        ? {
            amount: GAS_AIRDROP_CONFIG.defaultAmount,
            recipient: address as `0x${string}`,
          }
        : undefined,
    [includeGasAirdrop, address, isOft],
  );

  const extraOptions = useMemo(
    () => buildExtraOptions(gasAirdrop),
    [gasAirdrop],
  );

  const { data: quoteOFTData } = useOftQuoteOFT(
    isSameChain || !isOft ? undefined : selectedChainId,
    parsedAmount,
  );

  const { data: quoteSendData, isLoading: isQuoteSendLoading, error: quoteSendError } = useOftQuoteSend(
    isSameChain || !isOft ? undefined : selectedChainId,
    parsedAmount,
    extraOptions,
  );

  // --- OFT Allowance (cross-chain OFT only) ---
  const { data: oftAllowanceData } = useOftAllowance(
    isSameChain || !isOft ? undefined : selectedChainId,
    address,
  );

  // --- CCTP hooks (cross-chain USDC only) ---
  // Pre-fetch CCTP allowance for all USDC cross-chain (regardless of mode) for instant switching
  const cctpEnabled = (isCctp || isUsdcCrossChain) && !isSameChain;
  const { data: cctpAllowanceData } = useCctpAllowance(
    cctpEnabled ? selectedChainId : undefined,
    address,
  );
  const cctpApprove = useCctpApprove(isCctp && !isSameChain ? selectedChainId : undefined);
  const cctpBurn = useCctpBurn(isCctp && !isSameChain ? selectedChainId : undefined);
  const { data: cctpAttestationData } = useCctpAttestation(
    isCctp ? (bridgeTxHash as `0x${string}` | undefined) : undefined,
    isCctp ? selectedChainId : undefined,
  );
  const cctpReceive = useCctpReceive(isCctp ? INK_DEPOSIT_CHAIN.chainId : undefined);

  // --- Stargate hooks (cross-chain USDC via StargateV2) ---
  const { data: stargateQuoteData, isLoading: isStargateQuoteLoading } = useStargateQuoteSend(
    isStargate && !isSameChain ? selectedChainId : undefined,
    parsedAmount,
  );
  const { data: stargateAllowanceData } = useStargateAllowance(
    isStargate && !isSameChain ? selectedChainId : undefined,
    address,
  );
  const stargateApprove = useStargateApprove(isStargate && !isSameChain ? selectedChainId : undefined);
  const stargateSend = useStargateSend(isStargate && !isSameChain ? selectedChainId : undefined);

  // --- Across hooks (bridge-first funding) ---
  const {
    data: preliminaryAcrossQuoteData,
    isLoading: isPreliminaryAcrossQuoteLoading,
    isError: isPreliminaryAcrossQuoteError,
  } = useAcrossQuote({
    sourceChainId: isAcross ? selectedChainId : undefined,
    destinationChainId: isAcross ? INK_DEPOSIT_CHAIN.chainId : undefined,
    inputToken: isAcross ? fundingRoute?.sourceTokenAddress : undefined,
    outputToken: isAcross ? fundingRoute?.outputTokenAddress : undefined,
    amount: isAcross ? parsedAmount : undefined,
    recipient: isAcross ? (address as `0x${string}` | undefined) : undefined,
  });
  const acrossBridgeInputAmount = useMemo(() => {
    if (!isAcross || !parsedAmount || !preliminaryAcrossQuoteData) {
      return parsedAmount;
    }

    return calculateAcrossGrossInputAmount({
      targetOutputAmount: parsedAmount,
      quotedInputAmount: parsedAmount,
      quotedOutputAmount: preliminaryAcrossQuoteData.outputAmount,
    });
  }, [isAcross, parsedAmount, preliminaryAcrossQuoteData]);
  const {
    data: acrossQuoteData,
    isLoading: isExecutableAcrossQuoteLoading,
    isError: isExecutableAcrossQuoteError,
  } = useAcrossQuote({
    sourceChainId: isAcross ? selectedChainId : undefined,
    destinationChainId: isAcross ? INK_DEPOSIT_CHAIN.chainId : undefined,
    inputToken: isAcross ? fundingRoute?.sourceTokenAddress : undefined,
    outputToken: isAcross ? fundingRoute?.outputTokenAddress : undefined,
    amount: isAcross ? acrossBridgeInputAmount : undefined,
    recipient: isAcross ? (address as `0x${string}` | undefined) : undefined,
  });
  const isAcrossQuoteLoading =
    isPreliminaryAcrossQuoteLoading || isExecutableAcrossQuoteLoading;
  const isAcrossQuoteError =
    isPreliminaryAcrossQuoteError || isExecutableAcrossQuoteError;
  const acrossExecute = useAcrossExecute(isAcross && !isSameChain ? selectedChainId : undefined);

  // --- Nado Endpoint allowance (on Ink, polls every 3s) ---
  const { data: nadoAllowance } = useTokenAllowanceForProduct({ productId });
  const nadoAllowanceRaw = useMemo(() => {
    if (!nadoAllowance) return undefined;
    try {
      return BigInt(nadoAllowance.toFixed(0));
    } catch {
      return undefined;
    }
  }, [nadoAllowance]);

  // --- OFT Mutations ---
  const oftApprove = useOftApprove(isSameChain || !isOft ? undefined : selectedChainId);
  const oftSend = useOftSend(isSameChain || !isOft ? undefined : selectedChainId);

  // --- Nado Endpoint mutations ---
  const nadoApprove = useExecuteApproveAllowanceForProduct();
  const nadoDeposit = useExecuteDepositCollateral();
  const sponsoredNado = useSponsoredNadoTransactions({
    connectedAddress: address as `0x${string}` | undefined,
    tokenAddress: inkToken?.tokenAddress,
  });

  const nadoApproveIsPending =
    nadoApprove.isPending || sponsoredNado.approve.isPending;
  const nadoApproveIsSuccess =
    nadoApprove.isSuccess || sponsoredNado.approve.isSuccess;
  const nadoDepositIsPending =
    nadoDeposit.isPending || sponsoredNado.deposit.isPending;
  const nadoDepositIsSuccess =
    nadoDeposit.isSuccess || sponsoredNado.deposit.isSuccess;
  const nadoDepositTxHash = nadoDeposit.data ?? sponsoredNado.deposit.data;

  // Track on-chain confirmation of the deposit tx (mutation succeeds on tx submission,
  // but the tx can still revert on-chain — we need to verify before showing success)
  const depositTxState = useOnChainTransactionState({ txHash: nadoDepositTxHash });
  const isDepositConfirmedOnChain = depositTxState.type === 'confirmed';
  const isDepositFailedOnChain = depositTxState.type === 'error';

  // --- Bridge status tracking (OFT + Stargate both use LZ v2 → same LZ Scan API) ---
  const { data: bridgeStatusData } = useOftBridgeStatus(
    (isOft || isStargate) ? bridgeTxHash : undefined,
  );
  const { addPendingBridge } = usePendingBridges(address);

  const acrossArrival = useMemo(() => {
    if (!isAcross || expectedOutputAmount === undefined || preBridgeInkBalance === undefined || !bridgeSubmittedAtMs) {
      return undefined;
    }

    return deriveAcrossArrivalState({
      expectedOutputAmount,
      preBridgeInkBalance,
      currentInkBalance: inkBalanceData?.raw,
      submittedAtMs: bridgeSubmittedAtMs,
      nowMs: arrivalNowMs,
      hasVerifiedFill: acrossFillConfirmed,
    });
  }, [
    isAcross,
    expectedOutputAmount,
    preBridgeInkBalance,
    bridgeSubmittedAtMs,
    inkBalanceData,
    arrivalNowMs,
    acrossFillConfirmed,
  ]);

  const isAcrossDelivered = isAcross && acrossArrival?.status === 'delivered';
  const isAcrossPartialArrival = isAcross && acrossArrival?.status === 'partial_arrival';
  const isAcrossArrivalTimeout = isAcross && acrossArrival?.status === 'arrival_timeout';
  const walletChangedDuringBridge = !!(
    bridgeRecipient &&
    address &&
    bridgeRecipient.toLowerCase() !== address.toLowerCase() &&
    bridgeTxHash &&
    !isAcrossDelivered
  );

  // --- Computed values ---
  // Nado requires minimum 5.0 USDT0 per deposit (5000000 raw with 6 decimals)
  const MINIMUM_DEPOSIT_RAW = BigInt("5000000");
  const isBelowMinimum =
    parsedAmount !== undefined &&
    parsedAmount > BigInt("0") &&
    parsedAmount < MINIMUM_DEPOSIT_RAW;

  const sourceRequiredAmount = isAcross ? acrossBridgeInputAmount : parsedAmount;
  const hasEnoughBalance =
    parsedAmount !== undefined &&
    sourceRequiredAmount !== undefined &&
    balanceData?.raw !== undefined &&
    sourceRequiredAmount <= balanceData.raw;

  const needsCrossChainApproval = useMemo(() => {
    if (isSameChain || !parsedAmount) return false;
    // Across handles approval internally — skip cross-chain approval check
    if (isAcross) return false;
    if (isCctp) {
      return cctpAllowanceData ? !cctpAllowanceData.isApproved(parsedAmount) : true;
    }
    if (isStargate) {
      return stargateAllowanceData?.allowance !== undefined && stargateAllowanceData.allowance < parsedAmount;
    }
    // OFT
    return oftAllowanceData?.allowance !== undefined && oftAllowanceData.allowance < parsedAmount;
  }, [isSameChain, parsedAmount, isAcross, isCctp, isStargate, cctpAllowanceData, stargateAllowanceData, oftAllowanceData]);

  const bridgeDelivered = !!(
    // Across: fill evidence must be paired with balance delta, or balance delta must reach expected output
    (isAcross && isAcrossDelivered) ||
    // OFT/Stargate: check LZ Scan delivery status
    (bridgeTxHash && (isOft || isStargate) && bridgeStatusData?.status === 'delivered') ||
    // CCTP: check receiveMessage completion
    (bridgeTxHash && isCctp && cctpReceiveComplete)
  );
  const canProceedToNado =
    bridgeDelivered ||
    (isAcrossPartialArrival && allowPartialDeposit);

  // After bridging, the Ink balance may be less than parsedAmount due to bridge fees.
  // Wallets like Rabby also cap approval amounts to the user's balance, so the
  // approval check and deposit must use the actual available amount, not the original input.
  const effectiveDepositAmount = useMemo(() => {
    if (!parsedAmount) return undefined;
    if (isAcross && canProceedToNado && expectedOutputAmount !== undefined && preBridgeInkBalance !== undefined && inkBalanceData) {
      return calculateAcrossAutoDepositAmount({
        expectedOutputAmount: allowPartialDeposit && acrossArrival?.delta ? acrossArrival.delta : expectedOutputAmount,
        targetDepositAmount: parsedAmount,
        preBridgeInkBalance,
        currentInkBalance: inkBalanceData.raw,
        nadoAllowance: nadoAllowanceRaw,
      });
    }
    if (!canProceedToNado || isSameChain || !inkBalanceData) return parsedAmount;
    // Cap to Ink balance (bridge fees reduce received amount), but never go to 0
    // (flaky RPC can return 0 balance momentarily)
    if (inkBalanceData.raw <= BigInt("0")) return parsedAmount;
    let capped = parsedAmount <= inkBalanceData.raw ? parsedAmount : inkBalanceData.raw;
    // Also cap to on-chain allowance — wallets like Rabby auto-reduce approval
    // amounts from maxUint256 to the user's current balance, so the deposit
    // must not exceed what was actually approved on-chain.
    if (nadoAllowanceRaw !== undefined && nadoAllowanceRaw > BigInt("0") && nadoAllowanceRaw < capped) {
      capped = nadoAllowanceRaw;
    }
    return capped;
  }, [
    parsedAmount,
    isAcross,
    canProceedToNado,
    expectedOutputAmount,
    preBridgeInkBalance,
    inkBalanceData,
    allowPartialDeposit,
    acrossArrival?.delta,
    isSameChain,
    nadoAllowanceRaw,
  ]);

  // Check if Nado Endpoint needs approval for the deposit amount
  const needsNadoApproval = useMemo(() => {
    if (!effectiveDepositAmount || effectiveDepositAmount === BigInt("0")) return false;
    if (!nadoAllowance) return true; // Assume needs approval if unknown
    try {
      // Use BigNumber's .lt() instead of converting to BigInt — bignumber.js
      // uses scientific notation for large values like maxUint256, which BigInt()
      // cannot parse (throws SyntaxError), causing needsApproval to stay true forever.
      return nadoAllowance.lt(effectiveDepositAmount.toString());
    } catch {
      return true;
    }
  }, [effectiveDepositAmount, nadoAllowance]);

  const isAcrossRouteUnavailable = !!(
    isAcross &&
    parsedAmount &&
    parsedAmount > BigInt("0") &&
    !isAcrossQuoteLoading &&
    (!acrossQuoteData || isAcrossQuoteError)
  );

  // --- Button state machine (matches tested logic in depositStateMachine.test.ts) ---
  const buttonState: NadoDepositButtonState = useMemo(() => {
    if (!parsedAmount || parsedAmount === BigInt("0")) return 'idle';
    if (isBelowMinimum) return 'idle';
    if (walletChangedDuringBridge) return 'wallet_mismatch';
    if (isAcrossRouteUnavailable && !bridgeTxHash) return 'route_unavailable';

    // Check bridge/deposit completion BEFORE balance check.
    // After an OFT send, the source chain balance drops to 0 and
    // hasEnoughBalance becomes false — but we still need to show
    // bridge progress and continue the deposit flow.

    // Post-bridge deposit flow
    if (canProceedToNado) {
      if (nadoDepositIsSuccess && isDepositConfirmedOnChain) return 'success';
      if (nadoDepositIsSuccess && isDepositFailedOnChain) return 'ready_deposit'; // tx reverted, retry
      if (nadoDepositIsSuccess || nadoDepositIsPending) return 'depositing';
      if (nadoApproveIsPending || (nadoApproveIsSuccess && needsNadoApproval)) return 'approving';
      if (!isOnInk) {
        if (isSwitchingChain) return 'switching_chain';
        return 'requires_switch_chain';
      }
      if (needsNadoApproval) return 'requires_approve';
      return 'ready_deposit';
    }

    if (isAcrossPartialArrival) return 'partial_arrival';
    if (isAcrossArrivalTimeout) return 'arrival_timeout';

    // Bridge in progress
    if (bridgeTxHash) {
      if (bridgeStatusData?.status === 'failed') return 'idle';
      if (isAcross ? acrossExecute.isPending : isCctp ? cctpBurn.isPending : isStargate ? stargateSend.isPending : oftSend.isPending) return 'bridging';
      return isAcross ? 'bridge_submitted' : 'bridge_pending';
    }

    // Bridge send pending (tx submitted but no hash yet)
    if (isAcross ? acrossExecute.isPending : isCctp ? cctpBurn.isPending : isStargate ? stargateSend.isPending : oftSend.isPending) return 'bridging';

    // Same-chain deposit in progress (balance may have dropped)
    if (nadoDepositIsSuccess && isDepositConfirmedOnChain) return 'success';
    if (nadoDepositIsSuccess && isDepositFailedOnChain) return 'ready_deposit';
    if (nadoDepositIsSuccess || nadoDepositIsPending) return 'depositing';

    // Pre-bridge/pre-deposit: balance must be sufficient
    if (!hasEnoughBalance) return 'idle';

    // Chain switch (pre-bridge)
    if (!isCorrectChain) {
      if (isSwitchingChain) return 'switching_chain';
      return 'requires_switch_chain';
    }

    // Cross-chain: bridge approve + send
    if (!isSameChain) {
      if (isAcross && isAcrossQuoteLoading) return 'quoting';
      if (isAcross && !acrossQuoteData) return 'route_unavailable';
      if (isCctp ? cctpApprove.isPending : isStargate ? stargateApprove.isPending : oftApprove.isPending) return 'approving';
      if (needsCrossChainApproval) return 'requires_approve';
      return 'ready_bridge';
    }

    // Same-chain (Ink): Nado approve + deposit
    if (nadoApproveIsPending || (nadoApproveIsSuccess && needsNadoApproval)) return 'approving';
    if (needsNadoApproval) return 'requires_approve';
    return 'ready_deposit';
  }, [
    parsedAmount,
    isBelowMinimum,
    hasEnoughBalance,
    canProceedToNado,
    bridgeTxHash,
    bridgeStatusData,
    isCorrectChain,
    isOnInk,
    isSwitchingChain,
    isSameChain,
    isCctp,
    isStargate,
    isAcross,
    isAcrossRouteUnavailable,
    isAcrossQuoteLoading,
    acrossQuoteData,
    isAcrossPartialArrival,
    isAcrossArrivalTimeout,
    walletChangedDuringBridge,
    needsCrossChainApproval,
    needsNadoApproval,
    cctpApprove.isPending,
    cctpBurn.isPending,
    cctpReceiveComplete,
    oftApprove.isPending,
    oftSend.isPending,
    stargateApprove.isPending,
    stargateSend.isPending,
    acrossExecute.isPending,
    nadoApproveIsPending,
    nadoApproveIsSuccess,
    nadoDepositIsPending,
    nadoDepositIsSuccess,
    isDepositConfirmedOnChain,
    isDepositFailedOnChain,
  ]);

  // --- Error handling ---
  function clearError() {
    setErrorMessage(undefined);
    setErrorDetails(undefined);
  }

  function setError(err: unknown) {
    const details = err instanceof Error ? err.message : String(err);
    setErrorDetails(details);
    setErrorMessage(getNadoDepositErrorMessage(details));
  }

  // Show error when deposit tx reverts on-chain
  useEffect(() => {
    if (isDepositFailedOnChain && !errorMessage) {
      setErrorMessage('Deposit transaction reverted on-chain — try again');
      setErrorDetails(`Deposit tx reverted: ${nadoDepositTxHash}`);
    }
  }, [isDepositFailedOnChain, errorMessage, nadoDepositTxHash]);

  // --- CCTP: auto-execute receiveMessage when attestation is complete ---
  useEffect(() => {
    if (!isCctp || !cctpAttestationData || cctpAttestationData.status !== 'complete') return;
    if (cctpReceiveComplete) return;
    if (!cctpAttestationData.message || !cctpAttestationData.attestation) return;

    cctpReceive.mutateAsync({
      message: cctpAttestationData.message,
      attestation: cctpAttestationData.attestation,
    }).then(() => {
      setCctpReceiveComplete(true);
    }).catch((err: unknown) => {
      console.error('[NadoDeposit] CCTP receiveMessage failed:', err);
      setError(err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCctp, cctpAttestationData, cctpReceiveComplete]);

  // Across arrival polling: source tx success is only bridge_submitted.
  useEffect(() => {
    if (!isAcross || !bridgeTxHash || !bridgeRecipient || bridgeDelivered || isAcrossPartialArrival || isAcrossArrivalTimeout) {
      return;
    }

    const poll = () => {
      setArrivalNowMs(Date.now());
      queryClient.invalidateQueries({
        queryKey: ['sourceTokenBalance', INK_DEPOSIT_CHAIN.chainId, bridgeRecipient, inkToken?.tokenAddress],
      });
    };

    poll();
    const intervalId = window.setInterval(poll, 3_000);
    return () => window.clearInterval(intervalId);
  }, [
    isAcross,
    bridgeTxHash,
    bridgeRecipient,
    bridgeDelivered,
    isAcrossPartialArrival,
    isAcrossArrivalTimeout,
    queryClient,
    inkToken?.tokenAddress,
  ]);

  // Force-invalidate Nado allowance query after approval mutation succeeds,
  // so the 3s polling gap doesn't leave the user stuck at "Approve"
  useEffect(() => {
    if (nadoApproveIsSuccess) {
      queryClient.invalidateQueries({ queryKey: tokenAllowanceQueryKey() });
    }
  }, [nadoApproveIsSuccess, queryClient]);

  // Force-refresh Ink balance immediately after bridge delivers,
  // so effectiveDepositAmount can cap to actual received amount
  useEffect(() => {
    if (canProceedToNado && !isSameChain) {
      queryClient.invalidateQueries({ queryKey: ['sourceTokenBalance', INK_DEPOSIT_CHAIN.chainId] });
    }
  }, [canProceedToNado, isSameChain, queryClient]);

  // --- Actions ---
  const stopFundingAutomationRun = useCallback(() => {
    lastAutomationSubmittedStateRef.current = undefined;
    setIsFundingAutomationActive(false);
  }, []);

  const resetFormSession = useCallback(() => {
    formSessionVersionRef.current += 1;
    stopFundingAutomationRun();
  }, [stopFundingAutomationRun]);

  const onAmountChange = useCallback((newAmount: string) => {
    resetFormSession();
    setAmount(newAmount);
    setBridgeTxHash(undefined);
    setAcrossFillTxHash(undefined);
    setCctpReceiveComplete(false);
    setAcrossFillConfirmed(false);
    setExpectedOutputAmount(undefined);
    setPreBridgeInkBalance(undefined);
    setBridgeRecipient(undefined);
    setBridgeSubmittedAtMs(undefined);
    setAllowPartialDeposit(false);
    clearError();
  }, [resetFormSession]);

  const onFractionSelected = useCallback(
    (fraction: number) => {
      const balanceForFraction = selectAcrossAvailableBalance({
        sourceBalance: balanceData,
        inkBalance: inkBalanceData,
        bridgeTxHash,
        canProceedToNado,
        preBridgeInkBalance,
        expectedOutputAmount,
      });
      if (!balanceForFraction) return;
      const fractionAmount = (balanceForFraction.raw * BigInt(Math.round(fraction * 10000))) / BigInt("10000");
      const formatted = (Number(fractionAmount) / 1e6).toFixed(6);
      resetFormSession();
      setAmount(formatted);
      if (canProceedToNado) {
        clearError();
        return;
      }
      setBridgeTxHash(undefined);
      setAcrossFillTxHash(undefined);
      setCctpReceiveComplete(false);
      setAcrossFillConfirmed(false);
      setExpectedOutputAmount(undefined);
      setPreBridgeInkBalance(undefined);
      setBridgeRecipient(undefined);
      setBridgeSubmittedAtMs(undefined);
      setAllowPartialDeposit(false);
      clearError();
    },
    [
      balanceData,
      bridgeTxHash,
      canProceedToNado,
      expectedOutputAmount,
      inkBalanceData,
      preBridgeInkBalance,
      resetFormSession,
    ],
  );

  const onBridgeModeChange = useCallback(
    (newBridgeMode: BridgeMode) => {
      resetFormSession();
      setBridgeMode(newBridgeMode);
    },
    [resetFormSession],
  );

  const onRefreshSourceBalance = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['sourceTokenBalance', selectedChainId, balanceAddress, selectedTokenAddress],
    });
  }, [balanceAddress, queryClient, selectedChainId, selectedTokenAddress]);

  /**
   * onSubmit handles ONE action per click based on the current buttonState.
   * After each action completes, the state machine naturally transitions
   * to the next state via query polling (allowance, bridge status, etc).
   */
  const onSubmit = useCallback(async () => {
    console.log('[NadoDeposit] onSubmit', { buttonState, parsedAmount: parsedAmount?.toString() });
    if (!parsedAmount || !address || !chainConfig) return;
    clearError();
    const submitSessionVersion = formSessionVersionRef.current;
    const isCurrentSubmitSession = () => formSessionVersionRef.current === submitSessionVersion;

    // Helper: execute the bridge send (used by both requires_approve and ready_bridge)
    async function executeBridge() {
      if (!isCurrentSubmitSession()) return;

      if (isAcross) {
        if (!acrossQuoteData?.quote) {
          setErrorMessage('Bridge quote not available — try again');
          return;
        }
        if (!address || !fundingRoute) {
          setErrorMessage('Funding route not available — try again');
          return;
        }
        const recipient = address as `0x${string}`;
        setExpectedOutputAmount(acrossQuoteData.outputAmount);
        setBridgeRecipient(recipient);
        setPreBridgeInkBalance(inkBalanceData?.raw ?? BigInt("0"));
        setAcrossFillConfirmed(false);
        setAcrossFillTxHash(undefined);
        setAllowPartialDeposit(false);
        console.log('[NadoDeposit] Bridging via Across Protocol...');
        const result = await acrossExecute.mutateAsync({
          quote: acrossQuoteData.quote,
          onProgress: (step, status, txHash) => {
            if (!isCurrentSubmitSession()) return;
            console.log('[Across]', step, status);
            if (step === 'deposit' && status === 'success') {
              if (txHash) {
                setBridgeTxHash(txHash);
              }
              setBridgeSubmittedAtMs(Date.now());
            }
            if (step === 'fill' && status === 'success') {
              if (txHash) {
                setAcrossFillTxHash(txHash);
              }
              setAcrossFillConfirmed(true);
            }
          },
        });
        if (!isCurrentSubmitSession()) return;
        if (result?.sourceTxHash) {
          setBridgeTxHash(result.sourceTxHash);
          setBridgeSubmittedAtMs((current) => current ?? Date.now());
          if (result.fillTxHash) {
            setAcrossFillTxHash(result.fillTxHash);
          }
          if (result.fillConfirmed) {
            setAcrossFillConfirmed(true);
          }
          addPendingBridge({
            srcTxHash: result.sourceTxHash,
            srcChainId: selectedChainId,
            amount: acrossBridgeInputAmount ?? parsedAmount!,
            timestamp: Date.now(),
          });
        }
        return;
      }
      if (isCctp) {
        console.log('[NadoDeposit] Burning USDC via CCTP...');
        const burnResult = await cctpBurn.mutateAsync({
          amount: parsedAmount!,
          destinationChainId: INK_DEPOSIT_CHAIN.chainId,
        });
        if (!isCurrentSubmitSession()) return;
        setBridgeTxHash(burnResult.txHash);
        addPendingBridge({
          srcTxHash: burnResult.txHash,
          srcChainId: selectedChainId,
          amount: parsedAmount!,
          timestamp: Date.now(),
        });
      } else if (isStargate) {
        if (!stargateQuoteData) {
          setErrorMessage('Bridge fee quote not available — try again');
          return;
        }
        console.log('[NadoDeposit] Bridging USDC via StargateV2...');
        const txHash = await stargateSend.mutateAsync({
          amountLD: parsedAmount!,
          minAmountLD: parsedAmount!,
          nativeFee: stargateQuoteData.nativeFee,
          lzTokenFee: stargateQuoteData.lzTokenFee,
        });
        if (!isCurrentSubmitSession()) return;
        setBridgeTxHash(txHash);
        addPendingBridge({
          srcTxHash: txHash,
          srcChainId: selectedChainId,
          amount: parsedAmount!,
          timestamp: Date.now(),
        });
      } else {
        if (!quoteSendData) {
          setErrorMessage('Bridge fee quote not available — try again');
          return;
        }
        const minAmountLD = quoteOFTData?.amountReceivedLD ?? parsedAmount!;
        console.log('[NadoDeposit] Sending OFT bridge...');
        const txHash = await oftSend.mutateAsync({
          amountLD: parsedAmount!,
          minAmountLD,
          nativeFee: quoteSendData.nativeFee,
          lzTokenFee: quoteSendData.lzTokenFee,
          extraOptions,
          gasAirdrop,
        });
        if (!isCurrentSubmitSession()) return;
        setBridgeTxHash(txHash);
        addPendingBridge({
          srcTxHash: txHash,
          srcChainId: selectedChainId,
          amount: parsedAmount!,
          timestamp: Date.now(),
        });
      }
    }

    try {
      switch (buttonState) {
        case 'requires_switch_chain': {
          // Switch to the correct chain (source for bridge, Ink for deposit)
          const targetChainId = canProceedToNado
            ? INK_DEPOSIT_CHAIN.chainId
            : selectedChainId;
          await switchChainAsync({ chainId: targetChainId });
          break;
        }

        case 'requires_approve': {
          if (canProceedToNado || isSameChain) {
            // Post-bridge or same-chain: approve token for Nado Endpoint on Ink
            console.log('[NadoDeposit] Approving', selectedTokenSymbol, 'for Nado Endpoint...');
            if (sponsoredNado.canSponsor) {
              await sponsoredNado.approve.mutateAsync({
                productId,
                amount: maxUint256,
              });
            } else {
              assertCanSubmitNadoTransaction({
                canSponsor: sponsoredNado.canSponsor,
                inkGasBalance: inkGasBalanceData?.raw,
              });
              await nadoApprove.mutateAsync({
                productId,
                amount: maxUint256,
              });
            }
          } else {
            // Pre-bridge: approve AND bridge in one click (single-sign UX)
            // 1. Approve token for bridge contract
            if (isCctp) {
              console.log('[NadoDeposit] Approving USDC for CCTP...');
              await cctpApprove.mutateAsync();
            } else if (isStargate) {
              console.log('[NadoDeposit] Approving USDC for StargateV2...');
              await stargateApprove.mutateAsync();
            } else {
              console.log('[NadoDeposit] Approving token for OFT...');
              await oftApprove.mutateAsync();
            }

            // 2. Immediately execute bridge (no second click needed)
            if (!isCurrentSubmitSession()) return;
            console.log('[NadoDeposit] Approve done, executing bridge...');
            await executeBridge();
          }
          break;
        }

        case 'ready_bridge': {
          // Fallback: if user lands here (e.g., already approved), just bridge
          await executeBridge();
          break;
        }

        case 'partial_arrival': {
          setAllowPartialDeposit(true);
          break;
        }

        case 'arrival_timeout': {
          setArrivalNowMs(Date.now());
          queryClient.invalidateQueries({
            queryKey: ['sourceTokenBalance', INK_DEPOSIT_CHAIN.chainId],
          });
          break;
        }

        case 'ready_deposit': {
          // Deposit to Nado subaccount on Ink — use effectiveDepositAmount
          // which is capped to Ink balance after bridging (bridge fees reduce received amount)
          const depositAmount = effectiveDepositAmount ?? parsedAmount;
          const nadoDepositAmount = toNadoProductAmount({
            amount: depositAmount,
            tokenDecimals: chainConfig.tokenDecimals,
          });
          console.log('[NadoDeposit] Depositing', nadoDepositAmount, selectedTokenSymbol, 'to Nado...');
          if (sponsoredNado.canSponsor) {
            await sponsoredNado.deposit.mutateAsync({
              productId,
              amount: nadoDepositAmount,
            });
          } else {
            assertCanSubmitNadoTransaction({
              canSponsor: sponsoredNado.canSponsor,
              inkGasBalance: inkGasBalanceData?.raw,
            });
            await nadoDeposit.mutateAsync({
              productId,
              amount: nadoDepositAmount,
              referralCode: undefined,
            });
          }
          console.log('[NadoDeposit] Deposit successful!');
          break;
        }

        default:
          console.log('[NadoDeposit] No action for state:', buttonState);
      }
    } catch (err) {
      if (!isCurrentSubmitSession()) return;
      console.error('[NadoDeposit] Action failed:', err);
      setIsFundingAutomationActive(false);
      setError(err);
    }
  }, [
    parsedAmount,
    address,
    chainConfig,
    buttonState,
    canProceedToNado,
    selectedChainId,
    switchChainAsync,
    isSameChain,
    isCctp,
    isStargate,
    isAcross,
    nadoApprove,
    cctpApprove,
    cctpBurn,
    oftApprove,
    quoteSendData,
    quoteOFTData,
    oftSend,
    stargateApprove,
    stargateSend,
    stargateQuoteData,
    acrossExecute,
    acrossQuoteData,
    extraOptions,
    gasAirdrop,
    addPendingBridge,
    amount,
    acrossBridgeInputAmount,
    nadoDeposit,
    sponsoredNado,
    productId,
    selectedTokenSymbol,
    effectiveDepositAmount,
    fundingRoute,
    inkBalanceData?.raw,
    inkGasBalanceData?.raw,
    queryClient,
  ]);

  const automationStatus = getNadoFundingAutomationStatus({
    flow: 'deposit',
    buttonState,
    featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
    isPrivyEmbeddedWallet,
    isActive: isFundingAutomationActive,
  });

  const runAutomationStep = useCallback(() => {
    if (automationSubmitInFlightRef.current) {
      return;
    }

    if (!shouldSubmitNadoFundingAutomationStep({
      flow: 'deposit',
      buttonState,
      featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
      isPrivyEmbeddedWallet,
      isActive: isFundingAutomationActive,
      errorMessage,
      lastSubmittedButtonState: lastAutomationSubmittedStateRef.current,
    })) {
      return;
    }

    lastAutomationSubmittedStateRef.current = buttonState;
    automationSubmitInFlightRef.current = true;
    Promise.resolve(onSubmit()).finally(() => {
      automationSubmitInFlightRef.current = false;
    });
  }, [buttonState, errorMessage, isFundingAutomationActive, isPrivyEmbeddedWallet, onSubmit]);

  const onStartFundingAutomation = useCallback(() => {
    if (automationSubmitInFlightRef.current) {
      return;
    }

    lastAutomationSubmittedStateRef.current = undefined;

    if (
      automationStatus.isAvailable &&
      !canStartNadoFundingAutomationStep({
        flow: 'deposit',
        buttonState,
        featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
        isPrivyEmbeddedWallet,
        isActive: true,
      })
    ) {
      return;
    }

    automationSubmitInFlightRef.current = true;
    clearError();

    if (!automationStatus.isAvailable) {
      Promise.resolve(onSubmit()).finally(() => {
        automationSubmitInFlightRef.current = false;
      });
      return;
    }

    setIsFundingAutomationActive(true);
    lastAutomationSubmittedStateRef.current = buttonState;
    Promise.resolve(onSubmit()).finally(() => {
      automationSubmitInFlightRef.current = false;
    });
  }, [automationStatus.isAvailable, buttonState, isPrivyEmbeddedWallet, onSubmit]);

  const onStopFundingAutomation = useCallback(() => {
    stopFundingAutomationRun();
  }, [stopFundingAutomationRun]);

  useEffect(() => {
    if (!automationStatus.shouldAutoSubmit) {
      return;
    }

    runAutomationStep();
  }, [automationStatus.shouldAutoSubmit, buttonState, runAutomationStep]);

  useEffect(() => {
    if (!isFundingAutomationActive) {
      return;
    }

    if (
      shouldStopNadoFundingAutomation({
        flow: 'deposit',
        buttonState,
        errorMessage,
      })
    ) {
      setIsFundingAutomationActive(false);
    }
  }, [buttonState, errorMessage, isFundingAutomationActive]);

  // --- Format display values ---
  const formatEth = (wei: bigint | undefined) => {
    if (wei === undefined || wei === null) return '0';
    if (wei === BigInt("0")) return '0';
    return (Number(wei) / 1e18).toFixed(6);
  };

  if (quoteSendError) {
    console.error('[useNadoDepositForm] quoteSend error:', quoteSendError);
  }

  // Formatted Across fee for the bridge mode selector
  const acrossFeeFormatted = acrossQuoteData
    ? `~${(Number(acrossQuoteData.relayFeeTotal) / 1e6).toFixed(4)} ${selectedTokenSymbol}`
    : undefined;
  const acrossExpectedOutputFormatted = acrossQuoteData
    ? (Number(acrossQuoteData.outputAmount) / 1e6).toFixed(4)
    : undefined;
  const receivedAmountFormatted = acrossArrival
    ? (Number(acrossArrival.delta) / 1e6).toFixed(4)
    : undefined;

  const formData: NadoDepositFormData = {
    selectedChainId,
    selectedTokenSymbol,
    amount,
    availableBalance:
      selectAcrossAvailableBalance({
        sourceBalance: balanceData,
        inkBalance: inkBalanceData,
        bridgeTxHash,
        canProceedToNado,
        preBridgeInkBalance,
        expectedOutputAmount,
      })?.formatted ?? '0.00',
    estimatedReceiveAmount: isAcross
      ? (acrossExpectedOutputFormatted ?? (amount || '0.00'))
      : quoteOFTData
        ? (Number(quoteOFTData.amountReceivedLD) / 1e6).toFixed(4)
        : amount || '0.00',
    lzFee: (isQuoteSendLoading || isStargateQuoteLoading || isAcrossQuoteLoading) && !isSameChain && parsedAmount
      ? 'Loading...'
      : isAcross
        ? (acrossFeeFormatted ?? '~0')
        : isCctp
          ? 'Free (CCTP)'
          : isStargate
            ? formatEth(stargateQuoteData?.nativeFee)
            : formatEth(quoteSendData?.nativeFee),
    gasAirdropAmount: gasAirdrop
      ? formatEth(gasAirdrop.amount)
      : '0',
    needsGasAirdrop: !!needsGasAirdrop && !isSameChain,
    isCorrectChain,
    buttonState,
    bridgeProvider: isAcross ? 'across' : isCctp ? 'cctp' : isOft ? 'oft' : isStargate ? 'stargate' : 'none',
    bridgeMode,
    showBridgeModeSelector: isUsdcCrossChain && !isAcrossFunding,
    acrossFee: acrossFeeFormatted,
    acrossExpectedOutput: acrossExpectedOutputFormatted,
    acrossFillEstimateSec: acrossQuoteData?.estimatedFillTimeSec,
    receivedAmount: receivedAmountFormatted,
    isAcrossQuoteLoading,
    isAcrossRouteUnavailable,
    inputError: isBelowMinimum ? `Minimum 5 ${selectedTokenSymbol} deposit` : undefined,
    errorMessage,
    errorDetails,
    isFundingAutomationAvailable: automationStatus.isAvailable,
    isFundingAutomationActive: automationStatus.isActive,
  };

  const formActions: NadoDepositFormActions = {
    onAmountChange,
    onFractionSelected,
    onBridgeModeChange,
    onRefreshSourceBalance,
    onSubmit,
    onStartFundingAutomation,
    onStopFundingAutomation,
  };

  // --- Bridge progress ---
  const bridgeProgress: NadoBridgeProgressData | undefined =
    // Across: source tx is progress; delivery is decided by Ink balance arrival.
    (isAcross && bridgeTxHash && chainConfig && !isSameChain)
      ? {
          status: isAcrossDelivered
            ? 'delivered'
            : isAcrossPartialArrival
              ? 'partial_arrival'
              : isAcrossArrivalTimeout
                ? 'arrival_timeout'
                : 'in_transit',
          sourceTxHash: bridgeTxHash,
          sourceChain: chainConfig,
          tokenSymbol: selectedTokenSymbol,
        }
    // OFT/Stargate/CCTP: use bridgeTxHash + LZ Scan / attestation status
    : (bridgeTxHash && chainConfig && !isSameChain)
      ? {
          status: isCctp
            ? (cctpReceiveComplete
                ? 'delivered'
                : cctpAttestationData?.status === 'complete'
                  ? 'delivered'
                  : 'in_transit')
            : (bridgeStatusData?.status === 'delivered'
                ? 'delivered'
                : bridgeStatusData?.status === 'failed'
                  ? 'failed'
                  : 'in_transit'),
          sourceTxHash: bridgeTxHash,
          sourceChain: chainConfig,
          tokenSymbol: selectedTokenSymbol,
        }
      : undefined;

  return { formData, formActions, bridgeProgress };
}
